import React, { useEffect, useState } from 'react';
import { Container, Typography, Grid, Card, CardContent, CardMedia, IconButton, Box, CircularProgress, Button } from '@mui/material';
import { Favorite, FavoriteBorder, ShoppingCart } from '@mui/icons-material';
import { collection, query, where, documentId, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { useDesign } from '../context/DesignContext';

// Define Product interface if not available, or use any
interface Product {
  id: string;
  name: string;
  price: number;
  images: string[];
  description: string;
  category: string;
}

const WishlistPage = () => {
  const { wishlist, toggleWishlist, isInWishlist } = useWishlist();
  const { addItem } = useCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { config } = useDesign();

  useEffect(() => {
    const fetchProducts = async () => {
      if (wishlist.length === 0) {
        setProducts([]);
        return;
      }

      setLoading(true);
      try {
        const chunks = [];
        for (let i = 0; i < wishlist.length; i += 10) {
          chunks.push(wishlist.slice(i, i + 10));
        }

        let allProducts: Product[] = [];
        for (const chunk of chunks) {
          const q = query(collection(db, 'products'), where(documentId(), 'in', chunk));
          const snapshot = await getDocs(q);
          const chunkProducts = snapshot.docs.map(doc => {
            const data = doc.data();
            return { 
              id: doc.id, 
              name: data.name || '',
              price: typeof data.price === 'number' ? data.price : 0,
              images: Array.isArray(data.images) ? data.images : [],
              description: data.description || '',
              category: data.categoryId ? String(data.categoryId) : '',
            } as Product;
          });
          allProducts = [...allProducts, ...chunkProducts];
        }
        setProducts(allProducts);
      } catch (error) {
        console.error("Error fetching wishlist products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [wishlist]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom sx={{ fontFamily: 'Giaza', textAlign: 'center', mb: 4 }}>
        Mi Lista de Deseos
      </Typography>

      {products.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Tu lista de deseos está vacía.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/catalog')} sx={{ mt: 2, bgcolor: config.primaryColor }}>
            Explorar Catálogo
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {products.map((product) => (
            <Grid item key={product.id} xs={12} sm={6} md={4} lg={3}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <IconButton
                  onClick={(e) => {
                    e.preventDefault();
                    toggleWishlist(product.id);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                    color: isInWishlist(product.id) ? 'primary.main' : 'action.active',
                    zIndex: 2,
                  }}
                >
                  {isInWishlist(product.id) ? <Favorite /> : <FavoriteBorder />}
                </IconButton>
                <CardMedia
                  component="img"
                  height="200"
                  image={product.images && product.images.length > 0 ? product.images[0] : '/placeholder.png'}
                  alt={product.name}
                  sx={{ cursor: 'pointer', objectFit: 'contain', p: 2 }}
                  onClick={() => navigate(`/product/${product.id}`)}
                />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography gutterBottom variant="h6" component="div" sx={{ fontFamily: 'Playfair Display', cursor: 'pointer' }} onClick={() => navigate(`/product/${product.id}`)}>
                    {product.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 'bold', mt: 'auto' }}>
                    ${product.price}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<ShoppingCart />}
                    fullWidth
                    sx={{ mt: 2, borderColor: config.primaryColor, color: config.primaryColor }}
                    onClick={() => addItem({ 
                      id: product.id, 
                      name: product.name, 
                      price: product.price, 
                      image: product.images?.[0] || '', 
                      description: product.description, 
                      category: product.category 
                    })}
                  >
                    Agregar
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default WishlistPage;
